// profile-panel.js — 个人信息浮窗（桌面右侧浮窗 / 移动全屏滑入，两端共用）
// 参考 lumeet 母项目 profile-panel / xiaque-profile / xiaque-avatar，合并为单一模块。
// 内容：头像（可换）→ 用户名（只展示）→ 签名（可编辑）→「账号」分组（管理后台[仅admin]/退出登录）。
// 头像上传：canvas 圆形裁剪 256×256 PNG → POST /api/auth/profile/avatar。

import toast from './toast.js';
import { get as getSessionCache, set as setSessionCache, clear as clearSessionCache } from './session-cache.js';
import { registerPanel, openPanel, closePanel, isOpen } from './panel-manager.js';

var SIG_MAX = 100;      // 签名长度上限
var AVATAR_SIZE = 256;  // 头像输出标准尺寸

var _body = null; // 当前浮窗正文容器（.profile-body）
var _currentPage = 'profile'; // 浮窗内当前页：'profile' 主页 / 'avatar' 更换头像子页

// ===== 头像渲染（有 avatar_url 用 <img>，无则用户名首字色块） =====
function _initial() {
  var cache = getSessionCache();
  var username = (cache && cache.gate && cache.gate.username) || '';
  return username ? username.charAt(0).toUpperCase() : '?';
}

function _avatarUrl() {
  var cache = getSessionCache();
  return (cache && cache.avatar && cache.avatar.avatar_url) || '';
}

// 把头像渲染进指定元素（清空后填 img 或首字）
function _fillAvatar(el, url, initial) {
  if (!el) return;
  el.textContent = '';
  if (url) {
    var img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.onerror = function() { img.remove(); el.textContent = initial || '?'; };
    el.appendChild(img);
  } else {
    el.textContent = initial || '?';
  }
}

// 对外：渲染单个头像节点（读当前 session 缓存）
export function renderAvatarInto(el) {
  _fillAvatar(el, _avatarUrl(), _initial());
}

// 对外：同步刷新两处头像（侧边栏 / 浮窗内；聊天面板 me 侧头像经下方事件通知刷新）
export function refreshAllAvatars() {
  renderAvatarInto(document.getElementById('sideUserAv'));
  var panelAv = _body && _body.querySelector('.profile-avatar');
  if (panelAv) _fillAvatar(panelAv, _avatarUrl(), _initial());
  // 通知聊天面板刷新已渲染的 me 侧头像（换头像后同步刷新；事件解耦，避免与 chat-entry 互相 import 成环）
  try { window.dispatchEvent(new CustomEvent('hami:avatar-changed')); } catch (e) { /* 忽略 */ }
}

// ===== SVG 图标 =====
function _iconAdmin() {
  return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';
}
function _iconLogout() {
  return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/></svg>';
}
function _iconChevron() {
  return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
}

// 渲染「个人信息」主体（头像卡 + 账号分组）
function _renderProfile(body) {
  _currentPage = 'profile';
  body.innerHTML = '';
  var cache = getSessionCache() || {};
  var gate = cache.gate || {};
  var username = gate.username || '';
  var role = gate.role || '';
  var signature = (cache.signature != null) ? String(cache.signature) : '';
  var avatarUrl = (cache.avatar && cache.avatar.avatar_url) || '';

  // 头像卡
  var card = document.createElement('div');
  card.className = 'profile-card';

  var avatar = document.createElement('div');
  avatar.className = 'profile-avatar';
  avatar.title = '更换头像';
  _fillAvatar(avatar, avatarUrl, _initial());
  var editHint = document.createElement('div');
  editHint.className = 'av-edit';
  editHint.textContent = '更换';
  avatar.appendChild(editHint);
  avatar.addEventListener('click', function() { _openAvatarPage(body); });
  card.appendChild(avatar);

  var name = document.createElement('div');
  name.className = 'profile-name';
  name.textContent = username || '未登录';
  card.appendChild(name);

  var sig = document.createElement('p');
  sig.className = 'profile-sig';
  _bindEditable(sig, signature);
  card.appendChild(sig);

  body.appendChild(card);

  // 账号分组
  var secTitle = document.createElement('div');
  secTitle.className = 'profile-sec-title';
  secTitle.textContent = '账号';
  body.appendChild(secTitle);

  var group = document.createElement('ul');
  group.className = 'profile-group';

  if (role === 'admin') {
    group.appendChild(_buildRow(_iconAdmin(), '管理后台', false, function() {
      window.location.href = '/admin.html';
    }));
  }
  group.appendChild(_buildRow(_iconLogout(), '退出登录', true, function() {
    _handleLogout();
  }));

  body.appendChild(group);

  var spacer = document.createElement('div');
  spacer.style.height = '24px';
  body.appendChild(spacer);
}

function _buildRow(iconHtml, label, danger, onClick) {
  var li = document.createElement('li');
  li.className = 'profile-row' + (danger ? ' danger' : '');
  var ic = document.createElement('span');
  ic.className = 'profile-row-ic';
  ic.innerHTML = iconHtml;
  li.appendChild(ic);
  var lab = document.createElement('span');
  lab.className = 'profile-row-label';
  lab.textContent = label;
  li.appendChild(lab);
  var arrow = document.createElement('span');
  arrow.className = 'profile-row-arrow';
  arrow.innerHTML = _iconChevron();
  li.appendChild(arrow);
  li.addEventListener('click', onClick);
  return li;
}

// ===== 签名行内编辑（contenteditable，Enter/blur 提交） =====

function _bindEditable(el, initialValue) {
  var value = initialValue;
  el.setAttribute('contenteditable', 'true');
  el.setAttribute('spellcheck', 'false');
  _refreshSig(el, value);

  el.addEventListener('paste', function(e) {
    e.preventDefault();
    var text = ((e.clipboardData || window.clipboardData).getData('text/plain') || '');
    text = text.replace(/[\r\n\u2028\u2029]+/g, ' ').trim();
    if (!text) return;
    document.execCommand('insertText', false, text);
    _truncateSig(el);
  });

  el.addEventListener('beforeinput', function(e) {
    if (!e.inputType || e.inputType.indexOf('insert') !== 0) return;
    if (e.isComposing) return;
    if (e.inputType === 'insertLineBreak') { e.preventDefault(); return; }
    var data = e.data || '';
    if (el.textContent.length + data.length > SIG_MAX) {
      e.preventDefault();
      var remain = SIG_MAX - el.textContent.length;
      if (remain > 0) document.execCommand('insertText', false, data.slice(0, remain));
    }
  });

  el.addEventListener('input', function(e) {
    if (e.isComposing) return;
    _truncateSig(el);
  });
  el.addEventListener('compositionend', function() { _truncateSig(el); });

  el.addEventListener('focus', function() {
    if (!value) {
      el.textContent = '';
      el.classList.remove('placeholder');
    }
  });

  el.addEventListener('blur', function() {
    var newVal = el.textContent.trim().replace(/[\r\n]+/g, ' ');
    if (newVal.length > SIG_MAX) newVal = newVal.slice(0, SIG_MAX);
    if (newVal === value) {
      _refreshSig(el, value);
      return;
    }
    var oldValue = value;
    value = newVal;
    _refreshSig(el, value);
    _saveSignature(newVal, function() {
      value = oldValue;
      _refreshSig(el, value);
    });
  });

  el.addEventListener('keydown', function(e) {
    if (e.key === 'Enter' && !e.isComposing && e.keyCode !== 229) {
      e.preventDefault();
      el.blur();
    } else if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      el.blur();
    }
  });
}

function _refreshSig(el, value) {
  el.textContent = value || '点击设置个性签名…';
  el.classList.toggle('placeholder', !value);
}

function _truncateSig(el) {
  if (el.textContent.length <= SIG_MAX) return;
  el.textContent = el.textContent.slice(0, SIG_MAX);
  try {
    var range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    var sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } catch (err) {}
}

function _saveSignature(value, rollback) {
  fetch('/api/auth/profile', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ signature: value })
  }).then(function(r) { return r.json(); })
    .then(function(data) {
      if (r_ok(data)) {
        toast('签名已保存');
        var cache = getSessionCache() || {};
        cache.signature = value;
        setSessionCache(cache);
      } else {
        toast(data.error || '保存失败');
        rollback();
      }
    })
    .catch(function() {
      toast('保存失败，请检查网络');
      rollback();
    });
}

// 辅助：判断接口是否成功（响应体含 ok 或未含 error）
function r_ok(data) {
  return data && (data.ok === true || data.error === undefined);
}

// ===== 退出登录 =====

function _handleLogout() {
  fetch('/api/auth/gate-logout', { method: 'POST', credentials: 'include' })
    .catch(function() {})
    .then(function() {
      clearSessionCache();
      window.location.href = '/login.html';
    });
}

// ===== 更换头像子视图 =====

function _openAvatarPage(body) {
  _currentPage = 'avatar';
  body.innerHTML = '';
  var wrapper = document.createElement('div');
  wrapper.className = 'profile-avatar-page';

  // 预览
  var preview = document.createElement('div');
  preview.className = 'profile-avatar-preview';
  var previewWrap = document.createElement('div');
  previewWrap.className = 'profile-avatar-preview-wrap';
  _fillAvatar(previewWrap, _avatarUrl(), _initial());
  var previewHint = document.createElement('p');
  previewHint.className = 'profile-avatar-hint';
  previewHint.textContent = '点击下方选项更换头像';
  preview.appendChild(previewWrap);
  preview.appendChild(previewHint);
  wrapper.appendChild(preview);

  // 选择来源
  var groupHeader = document.createElement('div');
  groupHeader.className = 'profile-avatar-group-header';
  groupHeader.textContent = '选择头像来源';
  wrapper.appendChild(groupHeader);

  var groupCard = document.createElement('ul');
  groupCard.className = 'profile-group';
  var chooseRow = _buildRow(
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="15" rx="2.5"/><circle cx="8.5" cy="10" r="1.6"/><path d="M3 16.5l5-4 4 3 3.5-2.5 5.5 4"/></svg>',
    '从相册选择', false, function() { fileInput.click(); }
  );
  groupCard.appendChild(chooseRow);
  wrapper.appendChild(groupCard);

  // 裁剪区
  var cropWrap = document.createElement('div');
  cropWrap.className = 'profile-avatar-crop';
  var canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  cropWrap.appendChild(canvas);
  var cropHint = document.createElement('p');
  cropHint.className = 'profile-avatar-crop-hint';
  cropHint.textContent = '拖动调整位置，双指/滚轮缩放';
  cropWrap.appendChild(cropHint);
  var btnRow = document.createElement('div');
  btnRow.className = 'profile-avatar-crop-btns';
  var cancelBtn = _avatarBtn('取消', false);
  var doneBtn = _avatarBtn('完成', true);
  btnRow.appendChild(cancelBtn);
  btnRow.appendChild(doneBtn);
  cropWrap.appendChild(btnRow);
  wrapper.appendChild(cropWrap);

  body.appendChild(wrapper);

  var fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.style.display = 'none';
  body.appendChild(fileInput);

  var cropState = { img: null, scale: 1, offsetX: 0, offsetY: 0, dragging: false, lastX: 0, lastY: 0, pinchDist: 0 };

  fileInput.addEventListener('change', function() {
    var file = fileInput.files && fileInput.files[0];
    if (!file) return;
    fileInput.value = '';
    if (!file.type || file.type.indexOf('image/') !== 0) {
      toast('请选择图片文件');
      return;
    }
    var reader = new FileReader();
    reader.onload = function() {
      var img = new Image();
      img.onload = function() {
        cropState.img = img;
        _resetCrop(cropState, img);
        _enterCrop();
        _drawCrop(cropState, canvas);
      };
      img.onerror = function() { toast('图片读取失败'); };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  function _enterCrop() {
    preview.style.display = 'none';
    groupHeader.style.display = 'none';
    groupCard.style.display = 'none';
    // 裁剪区 CSS 默认 display:none，这里必须显式设 block；
    // 设 '' 会清掉内联样式、元素仍被 CSS 隐藏，导致选完文件后浮窗整体空白。
    cropWrap.style.display = 'block';
  }
  function _exitCrop() {
    preview.style.display = '';
    groupHeader.style.display = '';
    groupCard.style.display = '';
    cropWrap.style.display = 'none';
    cropState.img = null;
    doneBtn.disabled = false;
    doneBtn.textContent = '完成';
  }

  cancelBtn.addEventListener('click', _exitCrop);

  _bindCropGestures(canvas, cropState, function() { _drawCrop(cropState, canvas); });

  doneBtn.addEventListener('click', function() {
    if (!cropState.img) return;
    doneBtn.disabled = true;
    doneBtn.textContent = '保存中…';
    // _cropToBlob 返回 Promise（内部 canvas.toBlob 是异步的），必须 await 拿到真 Blob 再上传；
    // 否则把 Promise 塞进 FormData 会抛 TypeError，导致头像永远上传失败。
    _cropToBlob(cropState)
      .then(function(blob) { return _uploadAvatar(blob); })
      .then(function(newUrl) {
        toast('头像已更新');
        _fillAvatar(previewWrap, newUrl, _initial());
        _exitCrop();
        _updateCacheAvatar(newUrl);
        refreshAllAvatars();
      })
      .catch(function(err) {
        _exitCrop();
        toast((err && err.message) || '头像保存失败');
      });
  });
}

function _avatarBtn(text, primary) {
  var b = document.createElement('button');
  b.type = 'button';
  b.textContent = text;
  b.className = 'profile-avatar-btn' + (primary ? ' profile-avatar-btn--primary' : '');
  return b;
}

function _resetCrop(state, img) {
  var coverScale = Math.max(AVATAR_SIZE / img.width, AVATAR_SIZE / img.height);
  state.scale = coverScale;
  state.offsetX = (AVATAR_SIZE - img.width * coverScale) / 2;
  state.offsetY = (AVATAR_SIZE - img.height * coverScale) / 2;
}

function _drawCrop(state, canvas) {
  var ctx = canvas.getContext('2d');
  var r = AVATAR_SIZE / 2;
  ctx.clearRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
  ctx.fillStyle = '#eee';
  ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
  ctx.save();
  ctx.beginPath();
  ctx.arc(r, r, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (state.img) {
    ctx.drawImage(state.img, state.offsetX, state.offsetY, state.img.width * state.scale, state.img.height * state.scale);
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(r, r, r - 0.5, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(0,0,0,0.25)';
  ctx.lineWidth = 2;
  ctx.stroke();
}

function _bindCropGestures(canvas, state, redraw) {
  function clampOffsets() {
    var r = AVATAR_SIZE / 2;
    var img = state.img;
    if (!img) return;
    var dw = img.width * state.scale;
    var dh = img.height * state.scale;
    var cx = state.offsetX + dw / 2;
    var cy = state.offsetY + dh / 2;
    var maxDx = Math.max(0, dw / 2 - r);
    var maxDy = Math.max(0, dh / 2 - r);
    cx = Math.min(r + maxDx, Math.max(r - maxDx, cx));
    cy = Math.min(r + maxDy, Math.max(r - maxDy, cy));
    state.offsetX = cx - dw / 2;
    state.offsetY = cy - dh / 2;
  }
  function getPos(e) {
    var rect = canvas.getBoundingClientRect();
    var scaleX = AVATAR_SIZE / rect.width;
    var scaleY = AVATAR_SIZE / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }
  function onDown(e) {
    state.dragging = true;
    var p = getPos(e); state.lastX = p.x; state.lastY = p.y;
    // 拖拽期间才临时绑定 document 监听，结束时解绑，避免重复打开头像页累积监听器
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }
  function onMove(e) {
    if (!state.dragging) return;
    var p = getPos(e);
    state.offsetX += p.x - state.lastX;
    state.offsetY += p.y - state.lastY;
    state.lastX = p.x; state.lastY = p.y;
    clampOffsets(); redraw();
  }
  function onUp() {
    state.dragging = false;
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  }
  function onWheel(e) {
    e.preventDefault();
    var factor = e.deltaY < 0 ? 1.1 : 0.9;
    _applyScale(state, factor);
    clampOffsets(); redraw();
  }
  var touches = [];
  function onTouchStart(e) {
    touches = Array.prototype.slice.call(e.touches);
    if (touches.length === 1) { state.dragging = true; var p = getPos(touches[0]); state.lastX = p.x; state.lastY = p.y; }
    else if (touches.length === 2) { state.dragging = false; state.pinchDist = _dist(touches[0], touches[1]); }
    e.preventDefault();
  }
  function onTouchMove(e) {
    touches = Array.prototype.slice.call(e.touches);
    if (touches.length === 1 && state.dragging) {
      var p = getPos(touches[0]);
      state.offsetX += p.x - state.lastX;
      state.offsetY += p.y - state.lastY;
      state.lastX = p.x; state.lastY = p.y;
      clampOffsets(); redraw();
    } else if (touches.length === 2) {
      var d = _dist(touches[0], touches[1]);
      if (state.pinchDist) { _applyScale(state, d / state.pinchDist); clampOffsets(); redraw(); }
      state.pinchDist = d;
    }
    e.preventDefault();
  }
  function onTouchEnd() { state.dragging = false; state.pinchDist = 0; touches = []; }
  function _dist(a, b) { var dx = a.clientX - b.clientX, dy = a.clientY - b.clientY; return Math.sqrt(dx * dx + dy * dy); }

  canvas.addEventListener('mousedown', onDown);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd);
}

function _applyScale(state, factor) {
  var img = state.img;
  if (!img) return;
  var minScale = Math.max(AVATAR_SIZE / img.width, AVATAR_SIZE / img.height);
  var newScale = state.scale * factor;
  newScale = Math.max(minScale, Math.min(minScale * 4, newScale));
  var realFactor = newScale / state.scale;
  var cx = AVATAR_SIZE / 2, cy = AVATAR_SIZE / 2;
  state.offsetX = cx - (cx - state.offsetX) * realFactor;
  state.offsetY = cy - (cy - state.offsetY) * realFactor;
  state.scale = newScale;
}

function _cropToBlob(state) {
  var out = document.createElement('canvas');
  out.width = AVATAR_SIZE;
  out.height = AVATAR_SIZE;
  var ctx = out.getContext('2d');
  ctx.save();
  ctx.beginPath();
  ctx.arc(AVATAR_SIZE / 2, AVATAR_SIZE / 2, AVATAR_SIZE / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (state.img) {
    ctx.drawImage(state.img, state.offsetX, state.offsetY, state.img.width * state.scale, state.img.height * state.scale);
  }
  ctx.restore();
  return new Promise(function(resolve, reject) {
    out.toBlob(function(blob) {
      if (blob) resolve(blob); else reject(new Error('canvas 转 Blob 失败'));
    }, 'image/png');
  });
}

function _uploadAvatar(blob) {
  return new Promise(function(resolve, reject) {
    var fd = new FormData();
    fd.append('avatar', blob, 'avatar.png');
    fetch('/api/auth/profile/avatar', {
      method: 'POST',
      credentials: 'include',
      body: fd
    }).then(function(r) {
      return r.json().then(function(data) { return { status: r.status, data: data }; });
    }).then(function(res) {
      if (res.status === 200 && res.data && res.data.success) {
        resolve(res.data.avatar_url);
      } else {
        reject(new Error((res.data && res.data.error) || '上传失败'));
      }
    }).catch(function(err) { reject(err); });
  });
}

function _updateCacheAvatar(newUrl) {
  var cache = getSessionCache() || {};
  cache.avatar = { has_avatar: true, avatar_url: newUrl };
  setSessionCache(cache);
}

// ===== 面板接入统一管理器 =====

// 正文构建：只负责内容区（头像卡 + 账号分组），外壳/头部/遮罩由 manager 提供
function _buildBody(container, ctx) {
  container.innerHTML = '';
  var body = document.createElement('div');
  body.className = 'profile-body';
  container.appendChild(body);
  _body = body;
  _currentPage = 'profile';
  _renderProfile(body);
}

registerPanel('profile', {
  title: '个人信息',
  buildBody: _buildBody,
  // 头部 < 按钮：头像子页 → 返回主页；主页 → 关闭浮窗
  onBack: function() {
    if (_currentPage === 'avatar') {
      if (_body) _renderProfile(_body);
    } else {
      closePanel();
    }
  },
  // 关闭时清内部引用，避免残留指向已移除节点
  onClose: function() {
    _body = null;
    _currentPage = 'profile';
  }
});

export function openProfilePanel() {
  // 已打开则不再重复打开（避免把「更换头像」子页误重置回主页）
  if (isOpen('profile')) return;
  openPanel('profile');
}
